import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Fragment, useEffect } from 'react';
import { Mixpanel } from '../../../services/Mixpanel';
import PremiumIconCrown from '../../../components/PremiumIconCrown';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}
const OPEN_UP_CLASSES = 'bottom-[50px]';

export function Dropdown({
  label,
  Icon,
  name,
  optionList,
  selected,
  setSelected,
  isDisabled,
  onOpen,
  onClose,
  onClear,
  extraClasses = '',
  openDirection = 'down',
  disablePremiumRows = true,
  premiumRow = {
    show: false,
    message: 'Get unlimited access',
    link: 'https://web.diffit.me/plans',
    type: 'premium',
    location: 'Dropdown',
  },
}) {
  const { isPremiumUser } = useDiffitUserContext();
  // Add in the premium row right before the first premium item
  if (premiumRow.show && !isPremiumUser) {
    let newOptionList = [...optionList];
    const indexOfFirstPremiumItem = newOptionList.findIndex((item) => item.isPremium);
    // dont add if no premium items
    if (indexOfFirstPremiumItem !== -1) {
      newOptionList.splice(indexOfFirstPremiumItem, 0, premiumRow);
    }
    optionList = newOptionList;
  }

  const shouldDisablePremiumRows = disablePremiumRows && !isPremiumUser;

  return (
    <Listbox
      className='text-sm w-full sm:w-auto'
      name={name}
      value={selected}
      openDirection={openDirection}
      onChange={(value) => {
        Mixpanel.track(`${name} Selected`, { from: selected.name, to: value.name });
        setSelected(value);
        document.activeElement.blur();
      }}
      disabled={isDisabled}>
      {({ open }) => {
        useEffect(() => {
          if (open) {
            onOpen?.();
          } else {
            onClose?.();
          }
        }, [open, onOpen, onClose]);

        return (
          <div>
            {label && (
              <Listbox.Label className='flex gap-x-1 items-center text-gray-700 text-xs pl-2 pe-2 me-3 mb-2'>
                {label}
              </Listbox.Label>
            )}
            <div className='relative'>
              <Listbox.Button
                className={`hover:bg-gray-100  ${extraClasses} rounded-lg w-full  ${isDisabled ? 'pointer-events-none' : 'cursor-pointer'} flex items-center justify-between sm:justify-between gap-x-1 py-3 sm:py-2 pr-2 pl-2`}
                onClick={() => {
                  Mixpanel.track(`${name} Clicked`, { from: selected.name });
                }}>
                <div className='flex items-center gap-x-2'>
                  {Icon && <Icon className='w-4 h-4 text-emerald-600' />}
                  <span className='truncate'>{selected.name || selected}</span>
                </div>
                {onClear ? (
                  <button
                    aria-label='Clear Template'
                    className=''
                    onClick={(e) => {
                      Mixpanel.track(`${name} Cleared`, { from: selected.name });
                      onClear(e);
                    }}>
                    <XMarkIcon className='h-4 w-4 text-gray-400' />
                  </button>
                ) : (
                  <div className='pointer-events-none'>
                    <ChevronDownIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
                  </div>
                )}
              </Listbox.Button>

              <Transition
                show={open}
                as={Fragment}
                leave='transition ease-in duration-0'
                enter='transition ease-in duration-100'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'>
                <Listbox.Options
                  className={`float-left min-w-full w-max absolute z-50 pt-1 max-h-40 2xl:max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm scrollbar-minimal ${
                    openDirection === 'up' ? OPEN_UP_CLASSES : ''
                  }`}>
                  {optionList.map((item, index) => {
                    // Check if this is a category header
                    if (item.isCategory) {
                      return (
                        <div
                          key={index}
                          className='px-3 py-2 text-[0.6rem] font-semibold text-gray-500 uppercase tracking-wider bg-gray-100'>
                          {item.name}
                        </div>
                      );
                    }

                    return (
                      <Listbox.Option
                        key={index}
                        className={({ active }) =>
                          classNames(
                            active ? 'bg-gray-100 text-gray-900' : 'text-gray-900',
                            'relative cursor-default select-none py-2 pl-3 sm:whitespace-nowrap max-w-xs sm:max-w-full',
                            `${item.isPremium ? 'pr-2' : 'pr-0'}`,
                            `${(item.isPremium && shouldDisablePremiumRows) || item.type === 'premium' ? 'bg-gray-100' : ''}`
                          )
                        }
                        disabled={
                          (item.isPremium && shouldDisablePremiumRows) || item.type === 'premium'
                        }
                        value={item}>
                        {({ selected, active }) => (
                          <>
                            <div className='flex flex-col'>
                              {item.type === 'premium' && (
                                <div className='flex items-center w-full gap-x-2'>
                                  <PremiumIconCrown
                                    wrapperClassName='flex-shrink-0'
                                    className='h-3 w-3'
                                  />
                                  <div>
                                    <span className='font-semibold text-xs'>
                                      {premiumRow.message}
                                    </span>
                                    <a
                                      href='https://web.diffit.me/plans'
                                      target='_blank'
                                      className='text-amber-700 text-xs hover:underline inline flex flex-row items-center gap-x-1 font-semibold'
                                      onClick={() => {
                                        Mixpanel.track('See plans clicked', {
                                          location: premiumRow.location,
                                          message: premiumRow.message,
                                        });
                                      }}>
                                      See plans
                                      <ChevronRightIcon className='h-3 w-3' />
                                    </a>
                                  </div>
                                </div>
                              )}
                              {item.type !== 'premium' && (
                                <span
                                  className={classNames(
                                    selected ? 'font-normal' : 'font-normal',
                                    'flex items-center w-full justify-between',
                                    `${item.isPremium && shouldDisablePremiumRows ? 'text-gray-400' : ''}`
                                  )}>
                                  {item.name}
                                  {item.isPremium && (
                                    <PremiumIconCrown
                                      wrapperClassName='shrink-0'
                                      className='h-3 w-3 ml-4'
                                      toolTipProps={{
                                        text: undefined,
                                        position: 'top',
                                        anchor: 'right',
                                      }}
                                    />
                                  )}
                                </span>
                              )}
                              {item.description && (
                                <span
                                  className={classNames(
                                    active ? 'text-gray-500' : 'text-gray-400',
                                    'truncate max-w-xs'
                                  )}>
                                  {item.description}
                                </span>
                              )}
                            </div>

                            {selected ? (
                              <span
                                className={classNames(
                                  active ? 'text-white' : 'text-emerald-600',
                                  'absolute inset-y-0 right-0 flex items-center pr-4'
                                )}></span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    );
                  })}
                </Listbox.Options>
              </Transition>
            </div>
          </div>
        );
      }}
    </Listbox>
  );
}
